/** Primitive geometry only; no row elements, card payloads or native handles are retained. */
export class MeasuredRowIndex {
  readonly keys: readonly string[];
  readonly geometryKey: string;
  readonly positions = new Map<string, number>();
  readonly #measured = new Map<string, number>();
  readonly #heights: Float64Array;
  readonly #tree: Float64Array;
  readonly #listeners = new Set<() => void>();

  constructor(
    keys: readonly string[],
    estimatedHeight: number,
    geometryKey: string,
    previous?: MeasuredRowIndex,
  ) {
    this.keys = keys;
    this.geometryKey = geometryKey;
    this.#heights = new Float64Array(keys.length);
    this.#tree = new Float64Array(keys.length + 1);
    for (const [index, key] of keys.entries()) {
      this.positions.set(key, index);
      const measured =
        previous?.geometryKey === geometryKey ? previous.#measured.get(key) : undefined;
      if (measured !== undefined) {
        this.#measured.set(key, measured);
      }
      const height = measured ?? estimatedHeight;
      this.#heights[index] = height;
      this.#add(index, height);
    }
  }

  #add(index: number, delta: number) {
    for (let i = index + 1; i < this.#tree.length; i += i & -i) {
      this.#tree[i] = this.#treeValue(i) + delta;
    }
  }

  #treeValue(index: number): number {
    const value = this.#tree[index];
    if (value === undefined) {
      throw new Error(`Measured row tree index ${index} is out of bounds`);
    }
    return value;
  }

  offset(index: number): number {
    let sum = 0;
    for (let i = Math.min(index, this.keys.length); i > 0; i -= i & -i) {
      sum += this.#treeValue(i);
    }
    return sum;
  }

  get totalHeight() {
    return this.offset(this.keys.length);
  }

  /** Row containing offset; returns the row count beyond the final row. O(log N). */
  rowAt(offset: number): number {
    if (offset <= 0) {
      return 0;
    }
    let index = 0;
    let total = 0;
    let bit = 1;
    while (bit * 2 < this.#tree.length) {
      bit *= 2;
    }
    for (; bit > 0; bit = Math.floor(bit / 2)) {
      const next = index + bit;
      if (next < this.#tree.length) {
        const height = this.#treeValue(next);
        if (total + height <= offset) {
          total += height;
          index = next;
        }
      }
    }
    return index;
  }

  endAt(offset: number): number {
    if (offset <= 0) {
      return 0;
    }
    const row = this.rowAt(offset);
    return Math.min(this.keys.length, row + (this.offset(row) < offset ? 1 : 0));
  }

  /** Returns the change in row height; updates cumulative geometry in O(log N). */
  measure(key: string, height: number): number {
    const position = this.positions.get(key);
    if (position === undefined || !Number.isFinite(height) || height <= 0) {
      return 0;
    }
    const previousHeight = this.#heights[position];
    if (previousHeight === undefined) {
      throw new Error(`Measured row height index ${position} is out of bounds`);
    }
    const difference = height - previousHeight;
    this.#measured.set(key, height);
    if (difference !== 0) {
      this.#heights[position] = height;
      this.#add(position, difference);
    }
    return difference;
  }

  publish = () => {
    for (const listener of this.#listeners) {
      listener();
    }
  };
  subscribe = (listener: () => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };
}
